import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import useStable from './useStable';

export type FormValidators<T> = {
  [x in keyof T]?: (input: T) => string | undefined;
};

/** @deprecated use FormValidators */
export type Validators<T> = FormValidators<T>;

/** @deprecated do not use */
export type Manipulators<T> = {
  [x in keyof T]?: (input: T) => T[keyof T];
};

export type Errors<T> = {
  [x in keyof T]?: string;
};

export type FormHook<T> = {
  /** The current state of the form */
  state: T;
  /** The changed fields of the form */
  changedState: Partial<T>;
  /** Errors for each field, empty if the form is NOT dirty */
  errors: Errors<T>;
  /** Errors for each field, regardless if dirty or not */
  alwaysErrors: Errors<T>;
  /** If the form has errors, regardless if dirty or not */
  hasErrors: boolean;
  /** If the form has changes */
  hasChanges: boolean;
  /** if true form is dirty. it can now show errors */
  dirty: boolean;
  /** Fill the form with new values (merge) */
  fill: (payload: Partial<T>) => void;
  /** Set a new value for the state, can use an object to replace the state, or a function that takes the previous state as first argument */
  set: Dispatch<SetStateAction<T>>;
  /** Replace the form with new values and set dirty and hasChanges back to false */
  replace: (payload: T) => void;
  /** Reset the form to its initialState */
  reset: () => void;
  /** Set the form as dirty */
  setDirty: Dispatch<SetStateAction<boolean>>;
  /** Find the first error message for the specified keys */
  findError: (...k: Array<keyof T>) => string | undefined;
};

export type FormOptions<T> = {
  alwaysShowError?: boolean;
  validators?: FormValidators<T>;
  /** @deprecated do not use*/
  manipulators?: Manipulators<T>;
  base?: T;
  /** default */
  def?: Partial<T>;
};

/** @deprecated use FormHook<T> */
export type ReturnOption<T> = FormHook<T>;

const EMPTY_ERRORS = Object.freeze({});

// Values below are recomputed every render — the `validators` callers pass are
// inline closures that may read enclosing scope, so they must run each time to
// stay correct — and `useStable` then keeps their reference steady while the
// contents are unchanged.
const useForms = <T extends Record<string, any>>(
  initialState: T,
  options: FormOptions<T> = {},
): FormHook<T> => {
  const { alwaysShowError, validators, base, def } = options;
  const [state, setState] = useState<T>(initialState);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [dirty, setDirty] = useState<boolean>(false);

  // Note: `base`/`def` are usually inline literals at the few call sites that use
  // them, so their identity churns and the compare correctly yields a new object.
  const combinedState = useStable<T>({ ...def, ...base, ...state } as T);
  const changedState = useStable<Partial<T>>({ ...def, ...state });

  const nextErrors = {} as Errors<T>;
  for (const [key, func] of Object.entries(validators ?? {})) {
    nextErrors[key as keyof T] = typeof func === 'function' ? func(combinedState) : undefined;
  }
  const alwaysErrors = useStable<Errors<T>>(nextErrors);

  const findError = useCallback(
    (...keys: Array<keyof T>): string | undefined => {
      return keys
        .map((k) => alwaysErrors[k])
        .find((err) => Boolean(err) && typeof err === 'string');
    },
    [alwaysErrors],
  );

  // Kept in a ref so `reset` stays referentially stable while preserving the
  // existing behaviour of resetting to the most recent `initialState`.
  const initialStateRef = useRef(initialState);
  initialStateRef.current = initialState;

  const replace = useCallback((payload: T) => {
    setState(payload);
    setHasChanges(false);
    setDirty(false);
  }, []);

  const fill = useCallback((payload: Partial<T>) => {
    setState((prev) => ({ ...prev, ...payload }));
    setHasChanges(true);
  }, []);

  const set = useCallback((input: SetStateAction<T>) => {
    setState(input);
    setHasChanges(true);
  }, []);

  const reset = useCallback(() => {
    setState(initialStateRef.current);
    setHasChanges(false);
    setDirty(false);
  }, []);

  const errors = !dirty && !alwaysShowError ? (EMPTY_ERRORS as Errors<T>) : alwaysErrors;
  const hasErrors = Object.values(alwaysErrors).some((err) => typeof err !== 'undefined');

  return useMemo(
    () => ({
      state: combinedState,
      changedState,
      errors,
      alwaysErrors,
      hasErrors,
      fill,
      reset,
      set,
      findError,
      dirty,
      setDirty,
      replace,
      hasChanges,
    }),
    [
      combinedState,
      changedState,
      errors,
      alwaysErrors,
      hasErrors,
      fill,
      reset,
      set,
      findError,
      dirty,
      replace,
      hasChanges,
    ],
  );
};

export default useForms;
